import Seo from '@/components/Seo';
import { useAdSuppression } from '@/hooks/useAdSuppression';

export default function PrivacyPage() {
    useAdSuppression();
    return (
        <>
            <Seo
                title="Privacy Policy"
                description="How UtilityTool handles your files, data and privacy."
                canonical="/privacy-policy"
            />
            <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16">
                <p className="text-sm text-slate-500">Legal</p>
                <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
                    Privacy Policy
                </h1>
                <p className="mt-2 text-slate-500 text-sm">Last updated: January 1, 2026</p>
                <div className="prose mt-6">
                    <p>
                        This policy explains what information UtilityTool (“we”, “us”, “our”)
                        collects when you use our website and tools, why we collect it, and
                        what choices you have. UtilityTool is operated from South Africa,
                        and this policy is aligned with the South African Protection of
                        Personal Information Act (POPIA), the EU General Data Protection
                        Regulation (GDPR), and the California Consumer Privacy Act (CCPA)
                        where each applies to you.
                    </p>

                    <h2>Who we are</h2>
                    <p>
                        <strong>UtilityTool</strong><br />
                        Route 82 Security Apartments, Unit 34<br />
                        Vereeniging Rd, Olifantsvlei 327-Iq<br />
                        Johannesburg South, 2091, South Africa<br />
                        Email: <a href="mailto:hello@utilitytool.co.za">hello@utilitytool.co.za</a>
                    </p>

                    <h2>Files you upload</h2>
                    <p>
                        When you use a UtilityTool tool that requires uploading a file (for
                        example, our Word to PDF converter, JPG to PNG converter, or Image
                        Compressor), that file is transmitted to our servers over HTTPS and
                        processed in an isolated worker. The file is stored on disk only for
                        as long as the conversion takes — typically seconds — and is deleted
                        automatically once the result has been sent back to your browser.
                    </p>
                    <p>
                        We do not open, read, log, share, sell, or otherwise access the
                        contents of the files you upload. We do not retain any copy of them
                        after processing.
                    </p>

                    <h2>Tools that run in your browser</h2>
                    <p>
                        Some UtilityTool tools (for example the Word Counter, Case Converter,
                        QR Code Generator, and Lorem Ipsum Generator) do all of their work
                        directly in your browser. Nothing you enter into those tools is ever
                        transmitted to us.
                    </p>

                    <h2>Basic analytics</h2>
                    <p>
                        We use privacy-respecting analytics to understand which tools people
                        use most, so we know where to spend our time. This analytics data is
                        aggregated — it doesn’t identify you personally — and includes things
                        like page views, referrer, approximate country, and device type.
                    </p>

                    <h2>Cookies and local storage</h2>
                    <p>
                        We use a small number of first-party cookies and local-storage entries
                        to remember your preferences (for example the last quality level you
                        used with the Image Compressor). We do not use tracking cookies for
                        cross-site advertising.
                    </p>

                    <h2>Third-party advertising</h2>
                    <p>
                        UtilityTool is supported by contextual advertising. Ad partners may set
                        cookies to serve relevant ads and measure ad performance. You can opt
                        out of personalised advertising through your browser’s privacy
                        settings or via the ad partner’s opt-out page.
                    </p>

                    <h2>Contact form</h2>
                    <p>
                        If you contact us via the contact form, we store your message and
                        email address so we can reply. We don’t use that address for
                        marketing, and we don’t add it to any mailing list.
                    </p>

                    <h2>Data retention</h2>
                    <p>
                        Uploaded files: deleted immediately after processing. Contact form
                        submissions: retained for up to 12 months to help us follow up on
                        recurring issues. Analytics: retained in aggregated form for up to 24
                        months.
                    </p>

                    <h2>Your rights</h2>
                    <p>
                        You can request access to, or deletion of, any personal information we
                        hold about you by emailing us at{' '}
                        <a href="mailto:hello@utilitytool.co.za">hello@utilitytool.co.za</a>. We will
                        respond within 30 days.
                    </p>

                    <h2>Changes to this policy</h2>
                    <p>
                        We may update this policy from time to time. If we make material
                        changes, we’ll update the “Last updated” date at the top of the page.
                    </p>

                    <h2>Contact</h2>
                    <p>
                        Questions about privacy, or requests to access or delete your
                        data, can be sent to{' '}
                        <a href="mailto:hello@utilitytool.co.za">hello@utilitytool.co.za</a>.
                        Under POPIA, you also have the right to lodge a complaint with
                        the South African Information Regulator at{' '}
                        <a href="https://inforegulator.org.za" target="_blank" rel="noreferrer">inforegulator.org.za</a>.
                    </p>
                </div>
            </section>
        </>
    );
}
